/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CallerIdLookupSourcesPage.tsx
 ??? Created: 2025-01-28
 ?? Description:
    Caller ID Lookup Sources list page with table view, search, pagination, and actions.
    Allows viewing, creating, editing, and deleting lookup sources.

 ?? Notes:
    - Domain-scoped (multi-tenant safe)
    - Supports search and pagination
    - Shows name, source type, URL, timeout, and status

 ?? Last Updated: 2025-01-28 by Rodrigo Cuadra
----------------------------------------------------------------*/

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNavigationStore } from '../../store/navigation';
import { useNotifications } from '../../hooks/useNotifications';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Edit, Trash2, Search, Copy } from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useLookupSourceList } from './hooks';
import { deleteLookupSource } from './api';
import { navigateToCallerIdLookupSourceDuplicate } from './callerIdLookupSourcesActions';
import { CallerIdLookupSource } from './types';

export default function CallerIdLookupSourcesPage() {
  const { t } = useI18n();
  const { setCurrentPageTitle } = useNavigationStore();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  // Set page title
  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  // Delete confirmation state
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; source: CallerIdLookupSource | null }>({
    isOpen: false,
    source: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  // Generate columns
  const availableColumns = useMemo(() => {
    return [
      {
        key: 'name',
        label: t('callerIdLookupSources.fields.name.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: CallerIdLookupSource) => (
          <div className="font-medium text-foreground">{item.name}</div>
        )
      },
      {
        key: 'sourceType',
        label: t('callerIdLookupSources.fields.sourceType.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: CallerIdLookupSource) => (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.sourceType === 'internal'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
              : item.sourceType === 'http'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : item.sourceType === 'opencnam'
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                  : item.sourceType === 'database'
                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                    : item.sourceType === 'ldap'
                      ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
            }`}>
            {t(`callerIdLookupSources.sourceTypes.${item.sourceType}`, item.sourceType)}
          </span>
        )
      },
      {
        key: 'url',
        label: t('callerIdLookupSources.fields.url.label'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: CallerIdLookupSource) => (
          <div className="text-sm text-muted-foreground truncate max-w-xs">
            {item.url || '-'}
          </div>
        )
      },
      {
        key: 'timeout',
        label: t('callerIdLookupSources.fields.timeout.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: CallerIdLookupSource) => (
          <span className="text-sm font-mono text-foreground">{item.timeout}s</span>
        )
      },
      {
        key: 'enabled',
        label: t('callerIdLookupSources.fields.enabled.label'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: CallerIdLookupSource) => (
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
            }`}>
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        )
      },
      {
        key: 'createdAt',
        label: t('common.createdAt'),
        visible: false,
        sortable: true,
        render: (_value: unknown, item: CallerIdLookupSource) => {
          const date = new Date(item.createdAt);
          const isValidDate = !isNaN(date.getTime());
          return (
            <span className="text-sm text-muted-foreground">
              {isValidDate ? date.toLocaleDateString() : 'N/A'}
            </span>
          );
        }
      },
      {
        key: 'actions',
        label: t('common.actions'),
        visible: true,
        sortable: false
      }
    ];
  }, [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useLookupSourceList({ initialColumns: availableColumns });

  // Handlers
  const handleCreate = () => {
    navigate('/pbx/incoming-tools/callerid-lookup-sources/new');
  };

  const handleEdit = (source: CallerIdLookupSource) => {
    navigate(`/pbx/incoming-tools/callerid-lookup-sources/${source.uuid ?? source.id}`);
  };

  const handleDuplicate = useCallback((source: CallerIdLookupSource) => {
    navigateToCallerIdLookupSourceDuplicate(navigate, source.uuid ?? source.id);
  }, [navigate]);

  const handleDeleteClick = useCallback((source: CallerIdLookupSource) => {
    setDeleteModal({ isOpen: true, source });
  }, []);

  // Confirm delete
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.source) return;

    setIsDeleting(true);
    try {
      await deleteLookupSource(deleteModal.source.id);
      showSuccess(t('callerIdLookupSources.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, source: null });
      refresh();
    } catch (error) {
      showError(error.message || t('callerIdLookupSources.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.source, showError, showSuccess, t, refresh]);

  // Render row actions
  const renderRowActions = useCallback((source: CallerIdLookupSource) => (
    <div className="flex items-center gap-1">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          type="button"
          onClick={() => handleEdit(source)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          type="button"
          onClick={() => handleDuplicate(source)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          type="button"
          onClick={() => handleDeleteClick(source)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleEdit, handleDuplicate, handleDeleteClick, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onAdd={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onColumnToggle={handleColumnToggle}
          title={t('callerIdLookupSources.title')}
          titleIcon={<Search className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          searchPlaceholder={t('callerIdLookupSources.searchPlaceholder')}
          renderActions={renderRowActions}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, source: null })}
        onConfirm={handleDeleteConfirm}
        title={t('callerIdLookupSources.messages.deleteTitle') || 'Delete Caller ID Lookup Source'}
        message={t('callerIdLookupSources.messages.confirmDelete', { name: deleteModal.source?.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

